import cn from 'classnames';
import {FC, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';
import iBnbChain from '../../../assets/images/bnb-chain.svg';
import hardhatIcon from '../../../assets/images/hardhat.png';
import iMetamask from '../../../assets/images/metamask-wallet.svg';
import iSelected from '../../../assets/images/selected-icon.svg';
import iWalletconnect from '../../../assets/images/walletconnect.svg';
import {ChainId, EChainName, switchNetwork} from '../../../lib/chains';
import {useGetProjectExternalsQuery} from '../../../redux/projects.api';
import {closeConnectModal, connect, disconnect} from '../../../redux/wallet.slice';
import {DEEPLINKS_MAP, Wallet} from '../../../sdk';
import iEthereum from '../../../sdk/src/assets/icons/ethereum-chain.svg';
import iPolygon from '../../../sdk/src/assets/icons/polygon-chain.svg';
import {Connector} from '../../../services/wallet.service';
import useFrame from '../../hooks/useFrame';
import {AppDispatch} from '../../store';
import Checkbox from '../checkbox/checkbox';
import './connect-modal.sass';

export const chainsConfig = [
  {
    title: 'BNB Chain',
    symbol: 'BNB_TESTNET',
    icon: iBnbChain,
    id: ChainId.BINANCE_TESTNET,
    devOnly: true,
  },
  {
    title: 'BNB Chain',
    symbol: 'BNB',
    icon: iBnbChain,
    id: ChainId.BINANCE_MAINNET,
  },
  // {
  //   title: "CSC",
  //   symbol: "CSC",
  //   icon: iCoinex,
  //   id: ChainId.CSC_MAINNET,
  // },
  {
    title: 'Hardhat',
    symbol: 'HARDHAT',
    icon: hardhatIcon,
    id: ChainId.HARDHAT,
    devOnly: true,
  },
  {
    title: 'Polygon',
    icon: iPolygon,
    symbol: 'MATIC',
    id: ChainId.POLYGON_MAINNET,
    unsupportedWallets: [],
    devOnly: true,
  },
  {
    title: 'Ethereum',
    symbol: EChainName.ETH,
    icon: iEthereum,
    id: ChainId.ETH_MAINNET,
    unsupportedWallets: [],
  },
  {
    title: 'Polygon',
    icon: iPolygon,
    symbol: 'MATIC_TESTNET',
    id: ChainId.POLYGON_TESTNET,
    unsupportedWallets: [],
    devOnly: true,
  },
];

const connectors = [
  {
    title: 'Metamask',
    icon: iMetamask,
    connectorId: Connector.Injected,
    // noMobile: true,
    href: DEEPLINKS_MAP[Wallet.Metamask],
  },
  {
    title: 'WalletConnect',
    icon: iWalletconnect,
    connectorId: Connector.WalletConnect,
  },
];

type Props = {className?: string};

const ConnectModal: FC<Props> = ({className = ''}) => {
  const dispatch = useDispatch<AppDispatch>();
  console.log('render modal');
  const {projectId} = useParams<{projectId: string}>();
  const {data: projectExternals} = useGetProjectExternalsQuery(projectId);
  const projectNetworkSymbols = useMemo(() => {
    return projectExternals?.filter(el => el.isActive).map(el => el.network?.symbol);
  }, [projectExternals]);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(true);
  const [selectedChain, setSelectedChain] = useState<number>(chainsConfig[0].id);
  const {closeFrame} = useFrame();

  const onChainClick = (chainConfig: any) => {
    if (!isTermsAccepted) return;

    setSelectedChain(chainConfig.id);
  };

  const onWalletClick = async ({href, connectorId, title}: typeof connectors[0]): Promise<void> => {
    if (!isTermsAccepted) return;

    if (connectorId === Connector.Injected && !window.ethereum && !window.parent.ethereum && href) {
      window.open(href, '_blank', 'noopener noreferrer');
      dispatch(closeConnectModal());
      return;
    }

    if (title !== 'WalletConnect') {
      const isError = await switchNetwork(selectedChain);
      if (isError && isError.result !== null && isError !== null) {
        // NOTE: imtoken return object, metamask return string
        dispatch(disconnect());
        return;
      }
    }
    await dispatch(connect(connectorId));
    window.location.reload();
    closeFrame();
  };

  return (
    <div className={cn('connect-modal', className)}>
      <div className="connect-modal__title">Connect Wallet</div>
      <div className="connect-modal__row">
        <div className="connect-modal__row-title">
          1. Accept{' '}
          <a href="https://quicktoken.org/terms" target="_blank" className="connect-modal__link" rel="noreferrer">
            Terms of Services
          </a>{' '}
          {/* and{' '}
          <a
            href="https://gagarin.world/GGR_Privacy_Policy_v01.pdf"
            target="_blank"
            className="connect-modal__link"
            rel="noreferrer"
          >
            Privacy Policy
          </a> */}
        </div>
        <Checkbox checked={isTermsAccepted} setChecked={setIsTermsAccepted}>
          I read and accept
        </Checkbox>
      </div>
      <div className="connect-modal__row">
        <div className="connect-modal__row-title">2. Choose Network</div>
        <div className={cn('connect-modal__list', {_disabled: !isTermsAccepted})}>
          {chainsConfig
            .filter(el => projectNetworkSymbols?.includes(el.symbol))
            .map((chain: any, i) => (
              <div className="connect-modal__item" key={'chain-item' + i} onClick={() => onChainClick(chain)}>
                <img src={chain.icon} alt={chain.title} className="connect-modal__icon" />
                {selectedChain === chain.id && (
                  <img src={iSelected} alt="selected" className="connect-modal__icon-selected" />
                )}
                <div className="connect-modal__item-title">{chain.title}</div>
              </div>
            ))}
        </div>
      </div>
      <div className="connect-modal__row">
        <div className="connect-modal__row-title">3. Choose Wallet</div>
        <div className={cn('connect-modal__list', {_disabled: !isTermsAccepted})}>
          {connectors
            // .filter(el => !(el.noMobile && isMobile))
            .map((connector, i) => (
              <div key={'wallet-item' + i} className="connect-modal__item" onClick={() => onWalletClick(connector)}>
                <img src={connector.icon} className="connect-modal__icon" />
                <div className="connect-modal__item-title">{connector.title}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ConnectModal;
